import axios from 'axios';
import { toRaw } from 'vue';
import JwtService from '@/core/services/JwtService';

/**
 * Remembers the request that produced each list array (`core/common/list/{name}` + filters),
 * so a select component that only receives the array as `options` can re-run the same request
 * with `filters[search]` and get the matching items from the server.
 *
 * Keyed by the raw array, so it works with the reactive proxies Vuex hands out.
 */
const listSources = new WeakMap();

function registerListSource(list, source) {
   if (Array.isArray(list)) listSources.set(toRaw(list), source);
}

/**
 * Returns `{ name, filters, columns, id, params, external }` for a list that came from the
 * ListModule, or `undefined` when the array was built elsewhere (mapped, filtered, static ...).
 */
export function getListSource(list) {
   if (!list || typeof list !== 'object') return undefined;
   return listSources.get(toRaw(list));
}

function listRequestPath(listName, external = false) {
   if (external)
      return listName === 'external-get-const-list' ? 'core/common/' : 'core/common/external-list/';
   if (listName === 'getConstList') return 'core/common/';
   return JwtService.isAdminDomain() ? 'admin-panal/common/list/' : 'core/common/list/';
}

function listRequestParams(options) {
   return {
      filters: options.filters ?? {},
      columns: options.columns ?? {},
      id: options.id ?? null,
      ...(options.params || {}),
   };
}

// Bulk list entries may carry filters in a query string ("clients?with_inactive=1").
function parseBulkListEntry(entry) {
   const [name, query] = String(entry).split('?');
   const filters = {};
   if (query) new URLSearchParams(query).forEach((value, key) => (filters[key] = value));
   return { name, filters };
}

const ListModule = {
   namespaced: true,

   state: {
      list: {},
      cacheLists: {},
   },

   mutations: {
      SET_LIST(state, payload) {
         state.list[payload.listName] = payload.list;
         state.cacheLists[payload.listName + JSON.stringify(payload.filters)] = payload.list;
      },
      RESET_LIST(state, payload) {
         state.list[payload.listName] = [];
      },
   },

   actions: {
      resetList({ commit }, options) {
         let listName = typeof options === 'object' ? options.name : options;
         commit('RESET_LIST', { listName: listName });
      },

      getListByName({ commit, state }, options) {
         let listName = typeof options === 'object' ? options.name : options;
         const path = listRequestPath(listName);

         if (
            !options.disableCache &&
            listName + JSON.stringify(options.filters ?? {}) in state.cacheLists
         ) {
            state.list[listName] =
               state.cacheLists[listName + JSON.stringify(options.filters ?? {})];

            return {
               data: {
                  result: {
                     list: state.cacheLists[listName + JSON.stringify(options.filters ?? {})],
                  },
               },
            };
         }

         return new Promise((resolve, reject) => {
            axios
               .get(path + listName, { params: listRequestParams(options) })
               .then((res) => {
                  const list =
                     listName === 'getConstList' ? res.data.result.lists : res.data.result.list;

                  registerListSource(list, {
                     name: listName,
                     filters: options.filters ?? {},
                     columns: options.columns,
                     id: options.id,
                     params: options.params,
                  });
                  commit('SET_LIST', {
                     listName,
                     list,
                     filters: options.filters ?? {},
                  });
                  resolve(res);
               })
               .catch((error) => {
                  reject(error);
               });
         });
      },

      /**
       * Same request as `getListByName`, plus `filters[search]`, so the server returns only the
       * items matching the search term. The result is returned to the caller and NOT stored in
       * `state.list`, so other components bound to the same list keep the full list.
       */
      searchListByName(_, { search, ...source }) {
         const name = source.name;
         const path = listRequestPath(name, source.external);
         const params = listRequestParams({
            ...source,
            filters: { ...(source.filters ?? {}), search },
         });

         return axios.get(path + name, { params }).then((res) => res.data.result.list ?? []);
      },

      getBulkListByName({ commit, state }, { lists = [], filters = {}, params }) {
         let path = 'core/common/list/bulklists?';
         const entries = lists.map(parseBulkListEntry);

         return new Promise((resolve, reject) => {
            axios
               .get(path + lists.map((n, index) => `lists[${index}]=${n}`).join('&'), { params })
               .then((res) => {
                  Object.entries(res.data.result.lists).map(([listName, listValue]) => {
                     const entry = entries.find((e) => e.name === listName);

                     // `filters` is only a cache key here; the server saw just the entry's query.
                     registerListSource(listValue, {
                        name: listName,
                        filters: entry?.filters || {},
                        params,
                     });
                     commit('SET_LIST', {
                        listName,
                        list: listValue,
                        filters: filters,
                     });
                  });
                  resolve(res);
               })
               .catch((error) => {
                  reject(error);
               });
         });
      },

      getExternalListByName({ commit, state }, options) {
         let listName = typeof options === 'object' ? options.name : options;
         const path = listRequestPath(listName, true);

         return new Promise((resolve, reject) => {
            axios
               .get(path + listName, {
                  params: {
                     filters: options.filters ?? {},
                     columns: options.columns ?? {},
                     id: options.id ?? null,
                  },
               })
               .then((res) => {
                  const list =
                     listName === 'external-get-const-list'
                        ? res.data.result.lists
                        : res.data.result.list;

                  registerListSource(list, {
                     name: listName,
                     filters: options.filters ?? {},
                     columns: options.columns,
                     id: options.id,
                     external: true,
                  });
                  commit('SET_LIST', {
                     listName: listName === 'external-get-const-list' ? 'getConstList' : listName,
                     list,
                     filters: options.filters ?? {},
                  });
                  resolve(res);
               })
               .catch((error) => {
                  reject(error);
               });
         });
      },

      getBulkExternalListByName({ commit, state }, { lists = [], filters = {} }) {
         let path = 'core/common/external-list/bulklists?';
         const entries = lists.map(parseBulkListEntry);

         return new Promise((resolve, reject) => {
            axios
               .get(path + lists.map((n, index) => `lists[${index}]=${n}`).join('&'), {})
               .then((res) => {
                  Object.entries(res.data.result.lists).map(([listName, listValue]) => {
                     const entry = entries.find((e) => e.name === listName);

                     registerListSource(listValue, {
                        name: listName,
                        filters: entry?.filters || {},
                        external: true,
                     });
                     commit('SET_LIST', {
                        listName,
                        list: listValue,
                        filters: filters,
                     });
                  });
                  resolve(res);
               })
               .catch((error) => {
                  reject(error);
               });
         });
      },

      clearCache({ commit, state }) {
         state.cacheLists = {};
      },
   },

   getters: {
      getListByName: (state) => (name) => {
         return state.list?.[name] ?? [];
      },
   },
};
export default ListModule;
